import React, { useEffect, useRef, useState } from "react";
import {
  getSmtpAccounts,
  addSmtpAccount,
  deleteSmtpAccounts,
  addSmtpAccountsBulk
} from "../api/api";
import axios from "axios";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SmtpAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [host, setHost] = useState("mail.hosting.reg.ru");
  const [port, setPort] = useState(465);
  const [secure, setSecure] = useState(true);
  const [proxy, setProxy] = useState("");

  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [preview, setPreview] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
    validItems: []
  });

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // ===== SMTP-детект =====
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("auto");
  const [detecting, setDetecting] = useState(false);
  const [detectResult, setDetectResult] = useState(null);

  useEffect(() => { loadAccounts(); }, []);

  // ===== Загрузка справочника провайдеров =====
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/smtp-accounts/providers");
        setProviders(res.data);
      } catch (err) {
        console.error("Ошибка загрузки провайдеров:", err);
      }
    })();
  }, []);

  const loadAccounts = async () => {
    const res = await getSmtpAccounts();
    setAccounts(res.data);
    setFilteredAccounts(res.data);
  };

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      const q = search.toLowerCase();
      setFilteredAccounts(
        accounts.filter(a => a.email.toLowerCase().includes(q))
      );
    }, 700);
  }, [search, accounts]);

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAccounts.map(a => a.id));
    }
    setSelectAll(!selectAll);
  };

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Удалить ${selectedIds.length} аккаунтов?`)) return;

    await deleteSmtpAccounts(selectedIds);
    setSelectedIds([]);
    setSelectAll(false);
    loadAccounts();
  };

  // ===== Автодетект SMTP по email =====
  const handleDetect = async () => {
    if (!emailRegex.test(email)) {
      alert("Сначала введите корректный email");
      return;
    }
    setDetecting(true);
    setDetectResult(null);
    try {
      const res = await axios.post("/smtp-accounts/detect", { email });
      const data = res.data;
      setDetectResult(data);

      if (data.host) {
        setHost(data.host);
        setPort(data.port);
        setSecure(data.secure);
      }
    } catch (err) {
      console.error("Detect error:", err);
      alert("Не удалось определить SMTP: " + (err.response?.data?.error || err.message));
    } finally {
      setDetecting(false);
    }
  };

  const handleProviderSelect = (value) => {
    setSelectedProvider(value);
    if (value === "auto") return;

    const p = providers.find(x => x.host === value);
    if (p) {
      setHost(p.host);
      setPort(p.port);
      setSecure(p.secure);
      setDetectResult(null);
    }
  };

  const handleAdd = async () => {
    if (!emailRegex.test(email)) {
      alert("Неверный email");
      return;
    }

    await addSmtpAccount({
      email,
      password,
      host,
      port: Number(port),
      secure,
      proxy: proxy || null
    });

    setEmail("");
    setPassword("");
    setProxy("");
    setDetectResult(null);
    setSelectedProvider("auto");
    loadAccounts();
  };

  const buildPreview = (parsed) => {
    const valid = [];
    let invalid = 0;

    parsed.forEach(acc => {
      if (emailRegex.test(acc.email) && acc.password && acc.host && acc.port) {
        valid.push({
          email: acc.email,
          password: acc.password,
          host: acc.host,
          port: Number(acc.port),
          secure: Boolean(acc.secure),
          proxy: acc.proxy || null
        });
      } else {
        invalid++;
      }
    });

    setPreview({
      total: parsed.length,
      valid: valid.length,
      invalid,
      validItems: valid
    });
  };

  const readFile = (file) => {
    if (!file.name.endsWith(".json")) {
      alert("Только .json файл");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!Array.isArray(parsed)) {
          alert("Ожидается массив");
          return;
        }
        setImportText(JSON.stringify(parsed, null, 2));
        buildPreview(parsed);
      } catch {
        alert("Ошибка JSON");
      }
    };
    reader.readAsText(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) readFile(file);
    e.target.value = "";
  };

  useEffect(() => {
    if (!importOpen) return;
    const drop = dropRef.current;
    const prevent = e => e.preventDefault();
    const onDrop = e => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) readFile(file);
    };
    drop.addEventListener("dragover", prevent);
    drop.addEventListener("drop", onDrop);
    return () => {
      drop.removeEventListener("dragover", prevent);
      drop.removeEventListener("drop", onDrop);
    };
  }, [importOpen]);

  const handleImport = async () => {
    if (!preview.validItems.length) {
      alert("Нет валидных аккаунтов");
      return;
    }

    try {
      const res = await addSmtpAccountsBulk(preview.validItems);
      if (res.data.skipped && res.data.skipped.length > 0) {
        alert(`Импорт завершен.\nУже существующие аккаунты не добавлены:\n${res.data.skipped.join("\n")}`);
      } else {
        alert("Импорт успешно завершен!");
      }
      setImportOpen(false);
      setImportText("");
      setPreview({ total: 0, valid: 0, invalid: 0, validItems: [] });
      loadAccounts();
    } catch (err) {
      console.error("IMPORT ERROR:", err);
      alert("Ошибка при импорте: " + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-8">
      {/* MODAL */}
      {importOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-3 md:p-4">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-semibold mb-4">
              Массовый импорт почт
            </h3>

            <div
              ref={dropRef}
              className="border-2 border-dashed border-gray-300 rounded-xl p-4 md:p-6 text-center hover:border-blue-400 transition mb-4"
            >
              <p className="text-gray-600 mb-2 text-sm md:text-base">
                Перетащите JSON файл с почтами сюда
              </p>
              <button
                onClick={() => fileInputRef.current.click()}
                className="text-blue-600 font-medium hover:underline text-sm md:text-base"
              >
                или выберите файл
              </button>

              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept=".json"
                onChange={handleFileInput}
              />
            </div>

            {/* ПОДСКАЗКА ФОРМАТА */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-xs md:text-sm">
              <div className="font-medium text-blue-900 mb-2">
                📋 Формат списка почт:
              </div>
              <pre className="text-[10px] md:text-xs bg-white border border-blue-200 rounded-lg p-2 md:p-3 overflow-x-auto text-gray-700">
{`[
  {
    "email": "anna@company.ru",
    "password": "пароль",
    "host": "smtp.yandex.ru",
    "port": 465,
    "secure": true
  }
]`}
              </pre>
              <div className="text-[10px] md:text-xs text-blue-700 mt-2">
                <b>Обязательные:</b> email, password, host, port<br/>
                <b>Опционально:</b> secure (true/false)
              </div>
            </div>

            <textarea
              rows={6}
              value={importText}
              onChange={e => {
                setImportText(e.target.value);
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (Array.isArray(parsed)) buildPreview(parsed);
                } catch {}
              }}
              className="w-full border border-gray-200 rounded-xl p-3 text-xs md:text-sm font-mono focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />

            <div className="mt-4 text-sm space-y-1">
              <div>Всего: {preview.total}</div>
              <div className="text-green-600">Валидных: {preview.valid}</div>
              <div className="text-red-500">Ошибок: {preview.invalid}</div>
            </div>

            <div className="mt-6 flex flex-col md:flex-row gap-2 md:justify-end">
              <button
                onClick={() => setImportOpen(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition order-2 md:order-1"
              >
                Отмена
              </button>
              <button
                onClick={handleImport}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition shadow-md order-1 md:order-2"
              >
                Импортировать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 md:mb-6">
        <h2 className="text-lg md:text-2xl font-bold">Ваши SMTP почты</h2>
        <button
          onClick={() => setImportOpen(true)}
          className="hidden md:inline-block px-4 py-2 rounded-xl bg-blue-600 text-white shadow-md hover:bg-blue-700 transition"
        >
          📥 Подключить из списка
        </button>
      </div>

      {/* SEARCH + DELETE */}
      <div className="bg-white rounded-2xl shadow-md p-3 md:p-4 mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <input
            placeholder="Поиск по email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />

          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition shadow text-sm md:text-base"
              >
                🗑️ Удалить ({selectedIds.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* DESKTOP: TABLE */}
      <div className="hidden md:block bg-white rounded-2xl shadow-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">
                <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} />
              </th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Host</th>
              <th className="px-4 py-3 text-left">Port</th>
              <th className="px-4 py-3 text-left">Сегодня</th>
              <th className="px-4 py-3 text-left">Всего</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map(acc => (
              <tr key={acc.id} className="border-t hover:bg-gray-50 transition">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(acc.id)}
                    onChange={() => toggleSelect(acc.id)}
                  />
                </td>
                <td className="px-4 py-3 font-medium text-gray-800">
                  {acc.email}
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs font-mono">
                  {acc.host || "—"}
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs font-mono">
                  {acc.port || "—"}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {acc.sentToday || 0}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {acc.sentTotal || 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredAccounts.length === 0 && (
          <div className="text-center text-gray-400 py-8 text-sm">
            Нет почт
          </div>
        )}
      </div>

      {/* MOBILE: CARDS */}
      <div className="md:hidden space-y-2">
        {filteredAccounts.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={toggleSelectAll}
              className="w-4 h-4"
            />
            <span className="text-sm text-gray-600">Выбрать все</span>
          </div>
        )}

        {filteredAccounts.length === 0 && (
          <div className="text-center text-gray-400 py-8 text-sm bg-white rounded-xl">
            Нет почт
          </div>
        )}

        {filteredAccounts.map(acc => (
          <div
            key={acc.id}
            className={`bg-white rounded-xl shadow-sm p-3 flex items-start gap-3 transition ${
              selectedIds.includes(acc.id) ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(acc.id)}
              onChange={() => toggleSelect(acc.id)}
              className="mt-1 w-4 h-4 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-800 text-sm truncate">
                {acc.email}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5 truncate font-mono">
                {acc.host || "—"}:{acc.port || "—"}
              </div>
              <div className="flex gap-3 mt-1 text-xs text-gray-500">
                <span>Сегодня: <b className="text-gray-700">{acc.sentToday || 0}</b></span>
                <span>Всего: <b className="text-gray-700">{acc.sentTotal || 0}</b></span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ADD FORM */}
      <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 mt-4 md:mt-8">
        <h3 className="text-base md:text-lg font-semibold mb-4">
          ➕ Добавить почту вручную
        </h3>

        {/* Email + кнопка Определить */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Email
          </label>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              placeholder="info@company.ru"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onBlur={() => { if (emailRegex.test(email)) handleDetect(); }}
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />
            <button
              type="button"
              onClick={handleDetect}
              disabled={detecting || !emailRegex.test(email)}
              className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-50 text-sm whitespace-nowrap"
            >
              {detecting ? "Определяем..." : "🔍 Определить SMTP"}
            </button>
          </div>

          {/* Результат детекта */}
          {detectResult && (
            <div className={`mt-2 p-2 rounded-lg text-xs border ${
              detectResult.confident
                ? "bg-green-50 border-green-200 text-green-800"
                : detectResult.error
                  ? "bg-red-50 border-red-200 text-red-800"
                  : "bg-yellow-50 border-yellow-200 text-yellow-800"
            }`}>
              {detectResult.confident && (
                <>
                  ✓ Определён провайдер: <b>{detectResult.provider}</b>
                  <br />
                  Хост: <code className="bg-white px-1 rounded">{detectResult.host}:{detectResult.port}</code>
                </>
              )}
              {!detectResult.confident && !detectResult.error && (
                <>
                  ⚠️ Не уверены. Возможно, хост: <code className="bg-white px-1 rounded">{detectResult.host}:{detectResult.port}</code>
                  <br />
                  MX-записи домена: <span className="font-mono">{(detectResult.mx || []).join(", ") || "—"}</span>
                  <br />
                  Проверьте в настройках вашей почты.
                </>
              )}
              {detectResult.error && (
                <>❌ {detectResult.error}. Введите хост вручную.</>
              )}
            </div>
          )}
        </div>

        {/* Провайдер из справочника */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Или выберите провайдера:
          </label>
          <select
            value={selectedProvider}
            onChange={e => handleProviderSelect(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
          >
            <option value="auto">Автоопределение по email (рекомендуется)</option>
            {providers.map(p => (
              <option key={p.host} value={p.host}>
                {p.name} — {p.host}:{p.port}
              </option>
            ))}
          </select>
        </div>

        {/* Password + Host + Port */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-3">
          <input
            placeholder="Пароль"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="border border-gray-200 rounded-xl px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <input
            placeholder="smtp.yandex.ru"
            value={host}
            onChange={e => setHost(e.target.value)}
            className="border border-gray-200 rounded-xl px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
          <input
            placeholder="465"
            type="number"
            value={port}
            onChange={e => setPort(e.target.value)}
            className="border border-gray-200 rounded-xl px-4 py-2 text-sm md:text-base focus:ring-2 focus:ring-blue-400 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-3 mb-3">
          <input
            type="checkbox"
            checked={secure}
            onChange={e => setSecure(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm text-gray-600">SSL / Secure</span>
        </div>

        <button
          onClick={handleAdd}
          className="w-full md:w-auto px-6 py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-700 transition shadow-md text-sm md:text-base"
        >
          Добавить почту
        </button>
      </div>
    </div>
  );
}